import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot, Bell, CheckSquare, FolderKanban, Home, LogOut, Settings, Star, X } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useLogout } from '@/hooks/use-auth';
import { useFavoriteProjects } from '@/hooks/use-projects';
import { useUnreadNotificationCount } from '@/hooks/use-notifications';
import { useI18n } from '@/lib/i18n';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface AppSidebarProps {
    open: boolean;
    onClose: () => void;
    activeProjectId?: string;
    children?: ReactNode;
}

function getInitials(name: string): string {
    return name.split(' ').map((part) => part[0]).join('').toUpperCase().slice(0, 2);
}

export function AppBrand({ onClick }: { onClick?: () => void }) {
    return (
        <Link to="/dashboard" onClick={onClick} className="text-lg font-semibold tracking-tight text-white">
            Project Manager
        </Link>
    );
}

export function AppSidebar({ open, onClose, activeProjectId, children }: AppSidebarProps) {
    const location = useLocation();
    const { user } = useAuthStore();
    const logout = useLogout();
    const { t } = useI18n();
    const { data: favoriteProjects = [] } = useFavoriteProjects();
    const { data: unreadCount = 0 } = useUnreadNotificationCount();
    const navItems = [
        { href: '/dashboard', label: t('nav.dashboard'), icon: Home },
        { href: '/projects', label: t('nav.projects'), icon: FolderKanban },
        { href: '/assistant', label: 'Assistant', icon: Bot },
        { href: '/tasks', label: t('nav.myTasks'), icon: CheckSquare },
        { href: '/notifications', label: t('nav.notifications'), icon: Bell, badge: unreadCount },
    ];

    const isActive = (href: string) => href === '/dashboard'
        ? location.pathname === href
        : location.pathname.startsWith(href);

    return (
        <aside className={cn(
            'fixed inset-y-0 left-0 z-50 flex h-full w-64 shrink-0 flex-col border-r border-white/10 bg-white/5 backdrop-blur-md transition-transform duration-200 md:static md:z-0 md:translate-x-0',
            open ? 'translate-x-0' : '-translate-x-full'
        )}>
            <div className="h-16 shrink-0 flex items-center justify-between px-6 border-b border-white/10">
                <AppBrand onClick={onClose} />
                <button onClick={onClose} className="md:hidden p-2 rounded-lg hover:bg-white/10" aria-label={t('common.close')}>
                    <X className="h-5 w-5" />
                </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 min-h-0">
                <div className="space-y-1">
                    {navItems.map((item) => (
                        <Link
                            key={item.href}
                            to={item.href}
                            onClick={onClose}
                            className={cn(
                                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                isActive(item.href) ? 'bg-primary/10 text-primary' : 'text-white/70 hover:bg-white/10 hover:text-white'
                            )}
                        >
                            <item.icon className="h-4 w-4" />
                            <span>{item.label}</span>
                            {item.badge !== undefined && item.badge > 0 && (
                                <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
                                    {item.badge > 99 ? '99+' : item.badge}
                                </span>
                            )}
                        </Link>
                    ))}
                </div>

                {favoriteProjects.length > 0 && (
                    <div className="pt-4 mt-4 border-t border-white/10">
                        <p className="px-3 text-xs font-medium text-white/50 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                            {t('nav.favorites')}
                        </p>
                        {favoriteProjects.map((project) => (
                            <Link
                                key={project.id}
                                to={`/projects/${project.id}`}
                                onClick={onClose}
                                className={cn(
                                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                    (activeProjectId || location.pathname.split('/')[2]) === project.id
                                        ? 'bg-white/10 text-white'
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                )}
                            >
                                {project.imageUrl ? (
                                    <img src={project.imageUrl} alt="" className="h-5 w-5 rounded object-cover" />
                                ) : (
                                    <span className="h-5 w-5 rounded flex items-center justify-center text-xs" style={{ backgroundColor: project.color || '#6366f1' }}>
                                        {project.iconEmoji || '📁'}
                                    </span>
                                )}
                                <span className="truncate">{project.name}</span>
                            </Link>
                        ))}
                    </div>
                )}

                {children}
            </nav>

            <div className="border-t border-white/10 p-3 shrink-0">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-white/10 overflow-hidden">
                            <Avatar className="h-8 w-8 shrink-0">
                                <AvatarImage src={user?.avatarUrl || undefined} />
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                    {user?.name ? getInitials(user.name) : '?'}
                                </AvatarFallback>
                            </Avatar>
                            <span className="flex-1 text-left min-w-0">
                                <span className="block font-medium text-white truncate">{user?.name}</span>
                                <span className="block text-xs text-white/50 truncate">{user?.email}</span>
                            </span>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem asChild>
                            <Link to="/settings" onClick={onClose} className="flex items-center gap-2">
                                <Settings className="h-4 w-4" />
                                {t('nav.settings')}
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => logout.mutate()} className="text-destructive focus:text-destructive">
                            <LogOut className="h-4 w-4 mr-2" />
                            {t('nav.signOut')}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </aside>
    );
}
